import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

export type SortDirection = 'asc' | 'desc';

export type Column<T> = {
  key: string;
  header: ReactNode;
  /** Cell renderer. */
  cell: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Fixed column width, e.g. `'140px'`. */
  width?: string;
  sortable?: boolean;
  /** Hides the column below the `lg` breakpoint. */
  hideOnNarrow?: boolean;
};

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  sort,
  onSortChange,
  empty,
  footer,
  className,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  sort?: { key: string; direction: SortDirection };
  onSortChange?: (key: string) => void;
  empty?: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  const align = (value: Column<T>['align']) =>
    value === 'right' ? 'text-right' : value === 'center' ? 'text-center' : 'text-left';

  return (
    <div
      className={cn(
        'overflow-hidden rounded-card border border-line bg-surface shadow-card',
        className
      )}
    >
      <div className="overflow-x-auto scroll-thin">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line bg-elevated/50">
              {columns.map((column) => {
                const active = sort?.key === column.key;
                const SortIcon = !active ? ArrowUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown;

                return (
                  <th
                    key={column.key}
                    scope="col"
                    style={column.width ? { width: column.width } : undefined}
                    aria-sort={
                      active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                    className={cn(
                      'whitespace-nowrap px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted',
                      align(column.align),
                      column.hideOnNarrow && 'hidden lg:table-cell'
                    )}
                  >
                    {column.sortable && onSortChange ? (
                      <button
                        type="button"
                        onClick={() => onSortChange(column.key)}
                        className={cn(
                          // `uppercase` is repeated here on purpose: browsers
                          // don't inherit text-transform into form controls, so
                          // a sortable header would otherwise render mixed-case
                          // next to its plain neighbours.
                          'inline-flex items-center gap-1.5 rounded uppercase tracking-wide transition hover:text-fg',
                          column.align === 'right' && 'flex-row-reverse',
                          active && 'text-fg'
                        )}
                      >
                        {column.header}
                        <SortIcon size={12} />
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (event.key === 'Enter') onRowClick(row);
                      }
                    : undefined
                }
                className={cn(
                  'border-b border-line last:border-b-0',
                  onRowClick && 'cursor-pointer transition-colors hover:bg-primary/[0.04]'
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      'px-5 py-3.5 align-middle',
                      align(column.align),
                      column.hideOnNarrow && 'hidden lg:table-cell'
                    )}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>

          {footer && (
            <tfoot>
              <tr className="border-t border-line bg-elevated/40">{footer}</tr>
            </tfoot>
          )}
        </table>
      </div>

      {rows.length === 0 && empty}
    </div>
  );
}
